// Phase 5 (web revival) — Stream C.
//
// Reactions widget for the right rail. Shows the aggregate that comes
// down on AssetDetailDTO (`{ emoji, count, reacted_by_me }[]`) and lets
// the viewer add or remove their own. PUT/DELETE both target
// /v1/assets/{id}/reactions/{emoji} with the emoji URL-encoded. WS
// rebroadcasts `reaction.added` / `reaction.removed` which the cache
// listener invalidates qk.asset(id), so we just rely on refetch — no
// optimistic ping-pong to deal with for emoji counts.

import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../../api/client';
import { qk } from '../../app/queryClient';
import type {
  AssetDetailDTO,
  ReactionAggregateDTO,
} from '../../api/types';

const PICKER_EMOJIS = ['❤️', '👍', '😂', '😮', '😢', '🎉', '🔥', '✨'] as const;

interface ReactionsPanelProps {
  asset: AssetDetailDTO;
}

export function ReactionsPanel({ asset }: ReactionsPanelProps) {
  const queryClient = useQueryClient();
  const [pickerOpen, setPickerOpen] = useState(false);

  const mutation = useMutation<void, ApiError, { emoji: string; remove: boolean }>({
    mutationFn: async ({ emoji, remove }) => {
      const path = `/v1/assets/${asset.id}/reactions/${encodeURIComponent(emoji)}`;
      if (remove) {
        await api.delete<void>(path);
      } else {
        await api.put<void>(path, {});
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qk.asset(asset.id) });
      queryClient.invalidateQueries({ queryKey: qk.assetReactions(asset.id) });
    },
  });

  function toggle(agg: ReactionAggregateDTO | { emoji: string; reacted_by_me: boolean }) {
    mutation.mutate({ emoji: agg.emoji, remove: agg.reacted_by_me });
    setPickerOpen(false);
  }

  return (
    <div className="border-t border-white/5 px-5 py-4">
      <div className="mb-2 flex items-center justify-between">
        <div className="text-[11px] uppercase tracking-[0.14em] text-neutral-500">
          Reactions
        </div>
        <button
          type="button"
          onClick={() => setPickerOpen((o) => !o)}
          aria-expanded={pickerOpen}
          className="rounded-md border border-neutral-700 px-2 py-0.5 text-xs text-neutral-300 hover:bg-neutral-800"
        >
          {pickerOpen ? 'Close' : '+ Add'}
        </button>
      </div>

      {pickerOpen && (
        <div className="mb-2 flex flex-wrap gap-1 rounded-md border border-neutral-800 bg-neutral-900 p-2">
          {PICKER_EMOJIS.map((emoji) => {
            const existing = asset.reactions.find((r) => r.emoji === emoji);
            return (
              <button
                key={emoji}
                type="button"
                disabled={mutation.isPending}
                onClick={() =>
                  toggle({
                    emoji,
                    reacted_by_me: existing?.reacted_by_me ?? false,
                  })
                }
                className={`rounded-md px-2 py-1 text-lg leading-none hover:bg-neutral-800 disabled:opacity-50 ${
                  existing?.reacted_by_me ? 'bg-neutral-800' : ''
                }`}
                aria-label={`React with ${emoji}`}
              >
                {emoji}
              </button>
            );
          })}
        </div>
      )}

      {asset.reactions.length === 0 ? (
        <p className="text-xs text-neutral-500">No reactions yet.</p>
      ) : (
        <div className="flex flex-wrap gap-1.5">
          {asset.reactions.map((r) => (
            <button
              key={r.emoji}
              type="button"
              disabled={mutation.isPending}
              onClick={() => toggle(r)}
              className={`flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs transition-colors disabled:opacity-50 ${
                r.reacted_by_me
                  ? 'border-sky-500/60 bg-sky-950/40 text-sky-200'
                  : 'border-neutral-700 bg-neutral-900 text-neutral-300 hover:bg-neutral-800'
              }`}
              aria-pressed={r.reacted_by_me}
              aria-label={`${r.emoji} reaction, ${r.count}`}
            >
              <span aria-hidden>{r.emoji}</span>
              <span>{r.count}</span>
            </button>
          ))}
        </div>
      )}

      {mutation.error && (
        <p className="mt-2 text-xs text-red-300">{mutation.error.reason}</p>
      )}
    </div>
  );
}
